import { useEffect } from 'react';
import { playSound } from '../../utils/utils';
import { getCoreLaserDuration } from './coreLaser';
import { coreColorCss, getCoreAppearance } from './coreAppearance';
import { MAX_HP } from './game';
import { CORE_MASSIVE_ATTACK_BONUS } from './coreDamage';
import './CoreCounterattack.css';
import './CorePhaseAttackEffects.css';
import HealthLossFragments from './HealthLossFragments';
import { usesMobileRendering } from './mobileRendering';

const CoreCounterattack = ({
  stage,
  corePhase = 'containment',
  outcome,
  damage,
  attackType,
  baseDamage,
  protocolSaved = false,
  decoyDamage = 0,
  absorbed = 0,
  hpBefore,
  hpAfter,
  hpCurrent,
  maxHp = MAX_HP,
  maxHpGained = 0,
  healed = 0,
  reduceMotion,
  fallback,
  showHealth = true,
}) => {
  const impacted = stage === 'bossImpact' || stage === 'bossOutcome';
  const appearance = getCoreAppearance(corePhase);
  const alternateAttack = ['shards', 'rings', 'arcs'].includes(
    appearance.attackStyle
  );
  const hp = hpCurrent ?? (impacted ? hpAfter : hpBefore);
  const massiveAttack = attackType === 'massiveAttack' && outcome !== 'miss';
  useEffect(() => {
    if (stage === 'bossCharge')
      playSound(
        massiveAttack
          ? '/sounds/massive-attack.mp3'
          : '/sounds/core-charging-attack.mp3',
        { preventOverlap: true }
      );
  }, [stage, massiveAttack]);
  const attackName =
    attackType === 'massiveAttack' ? 'MASSIVE ATTACK' : appearance.attackName;
  const label = !impacted
    ? stage === 'bossCharge'
      ? `${attackName} CHARGING`
      : `${attackName} INBOUND`
    : protocolSaved
    ? 'LAST STAND · 1 HP'
    : outcome === 'hit'
    ? `−${damage} HP`
    : outcome === 'block'
    ? 'ATTACK BLOCKED'
    : outcome === 'decoy'
    ? 'DECOY INTERCEPTED'
    : 'CORE MISSED';
  return (
    <div
      className={`coreboundCoreCounterattack phase-${corePhase} attack-${
        appearance.attackStyle
      } is-${outcome} at-${stage} ${
        massiveAttack ? 'has-massive-attack' : ''
      } ${reduceMotion ? 'is-reduced' : ''} ${fallback ? 'is-fallback' : ''} ${
        !showHealth ? 'has-canvas-health' : ''
      }`}
      data-stage={stage}
      data-mobile-rendering={usesMobileRendering() || undefined}
      data-outcome={outcome}
      aria-label="Glitch Core counterattack"
      style={{
        '--laser-stage-duration': `${getCoreLaserDuration(
          stage,
          reduceMotion,
          massiveAttack ? attackType : null
        )}ms`,
        '--hp-after-width': `${(hpAfter / maxHp) * 100}%`,
        '--core-attack-color': coreColorCss(appearance.attack),
        '--core-attack-accent': coreColorCss(appearance.accent),
        '--core-attack-hot': coreColorCss(appearance.hot),
      }}
    >
      <div
        key={`${stage}-effects`}
        className="coreboundLaserEffects"
        aria-hidden="true"
      >
        <div className="coreboundLaserChargeRings">
          <i />
          <i />
          <i />
        </div>
        <div className="coreboundLaserFallbackBeam">
          <i />
          <i />
          <i />
        </div>
        {alternateAttack && (
          <div className="coreboundPhaseAttackVisuals">
            <div className="coreboundPhaseAttackFlight">
              {appearance.attackStyle === 'arcs' ? (
                <svg viewBox="0 0 1000 1000" preserveAspectRatio="none">
                  <path d="M500 370Q-200 500 500 1000" />
                  <path d="M500 370Q1200 500 500 1000" />
                  <path d="M500 370Q800 100 500 1000" />
                </svg>
              ) : (
                Array.from(
                  { length: appearance.attackStyle === 'shards' ? 7 : 4 },
                  (_, index) => <i key={index} style={{ '--shot': index }} />
                )
              )}
            </div>
            <div className="coreboundPhaseAttackImpact">
              <i />
              <i />
              <i />
            </div>
          </div>
        )}
        <div className="coreboundLaserImpactVignette" />
        {corePhase === 'meltdown' && (
          <div className="coreboundLaserRuptureRings">
            <i />
            <i />
            <i />
          </div>
        )}
        {corePhase === 'meltdown' && (
          <svg
            className="coreboundLaserHeartFractures"
            viewBox="0 0 1000 1000"
            preserveAspectRatio="none"
          >
            <path d="M0 70 140 200 110 290 260 360M1000 100 840 190 890 300 740 380M0 750 140 690 180 580 300 540M1000 800 850 720 880 620 720 550M220 0 310 100 290 180M740 1000 680 860 710 780" />
          </svg>
        )}
        <div className="coreboundLaserScreenTears">
          <i />
          <i />
          <i />
          <i />
        </div>
        <div className="coreboundLaserShield">
          <svg viewBox="0 0 200 200">
            <path d="M100 6 181 53V147L100 194 19 147V53Z M100 6V194 M19 53 181 147 M181 53 19 147 M19 53H181 M19 147H181" />
          </svg>
          <span />
        </div>
        <div className="coreboundLaserDebris">
          {Array.from({ length: 16 }, (_, index) => (
            <i
              key={index}
              style={{
                '--fragment-angle': `${index * 22.5}deg`,
                '--fragment-distance': `${90 + (index % 4) * 35}px`,
              }}
            />
          ))}
        </div>
        <div className="coreboundLaserMissStreak" />
        {outcome === 'decoy' && fallback && (
          <>
            <svg
              className="coreboundLaserDecoyBeam"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
            >
              <path d="M0 100 100 0" />
              <path d="M0 100 100 0" />
            </svg>
            <div className="coreboundLaserDecoy">
              <svg viewBox="0 0 100 150">
                <circle cx="50" cy="23" r="15" />
                <path d="M30 48H70L85 95H67L61 137H39L33 95H15Z M30 48L67 95M70 48L33 95M39 137L67 95M61 137L33 95" />
              </svg>
              <span>HOLOGRAM</span>
            </div>
          </>
        )}
      </div>
      {fallback && impacted && outcome === 'hit' && (
        <div className="coreboundContactImpact" aria-hidden="true">
          <i className="coreboundContactBurst" />
          <i className="coreboundContactWave" />
          {Array.from({ length: 6 }, (_, index) => (
            <i
              key={index}
              className="coreboundContactSmoke"
              style={{ '--puff': index }}
            />
          ))}
        </div>
      )}
      <div className="coreboundLaserPlayerHud">
        {showHealth && (
          <>
            <span className="coreboundLaserHudKicker">
              YOUR VITALS / ROUND IMPACT
            </span>
            <div className="coreboundLaserHpReadout">
              <span>HP</span>
              <strong>
                {hp} / {maxHp}
              </strong>
            </div>
            <div
              className="coreboundLaserHpBar"
              role="progressbar"
              aria-label="Your HP"
              aria-valuemin={0}
              aria-valuemax={maxHp}
              aria-valuenow={hp}
            >
              <HealthLossFragments
                hp={hp}
                maxHp={maxHp}
                reduceMotion={reduceMotion}
              />
              <span
                className="coreboundLaserHpTrail"
                style={{ width: `${(hpBefore / maxHp) * 100}%` }}
              />
              <span
                className="coreboundLaserHpRemaining"
                style={{ width: `${(hp / maxHp) * 100}%` }}
              />
            </div>
          </>
        )}
        <div
          key={`${stage}-label`}
          className="coreboundLaserDamageLabel"
          role="status"
          aria-live={stage === 'bossImpact' ? 'assertive' : 'polite'}
        >
          <strong>{label}</strong>
          <small>
            {!impacted
              ? outcome === 'decoy'
                ? 'Your beacon is broadcasting a false player signal.'
                : attackType === 'massiveAttack' && outcome !== 'miss'
                ? stage === 'bossCharge'
                  ? `Reactor overdrive: +${CORE_MASSIVE_ATTACK_BONUS} HP damage. Your defenses still apply.`
                  : `Damage locked: ${baseDamage} HP damage · your defenses still apply.`
                : 'The Core is locking onto your signal.'
              : protocolSaved
              ? 'Your survival roll beat 75. Last Stand Protocol consumed.'
              : outcome === 'hit'
              ? absorbed > 0
                ? `${absorbed} damage blocked · ${damage} damage got through.`
                : 'Your signal took a direct hit.'
              : outcome === 'block'
              ? absorbed > 0
                ? `Your defenses absorbed ${absorbed} damage.`
                : 'The attack could not breach your defenses.'
              : outcome === 'decoy'
              ? `Your hologram took the hit · ${decoyDamage} HP damage prevented.`
              : appearance.missText}
          </small>
        </div>
        {healed > 0 && (
          <small className="coreboundLaserHealing">
            + {healed} HP restored before the counterattack
            {maxHpGained > 0 ? ` · Maximum increased by ${maxHpGained}` : ''}
          </small>
        )}
      </div>
    </div>
  );
};

export default CoreCounterattack;
